import { NotificationManager } from 'react-notifications';
import axios, { AxiosError } from 'axios';
import qs from 'query-string';
import { ISelectOptions } from 'src/components/ui/Select/createSelect.types';
import { isApiErrorResponse } from 'src/helpers/errors';
import { IQueryParams } from 'src/types';
import { IErrorResponse } from 'src/types/errors';

import { IMerchantAccount } from '../connectedProviders/accountsPayment/types';
import { IMerchant } from '../merchants/types';

import { IPaymentForm, IPaymentRoutingData, IPaymentSystemGroupForm, ISite, TArchivingStatusParams, TRoutingStatusParams } from './types';

export const fetchPaymentRoutingRequest = async ({ page, size, sort, ...data }: IQueryParams): Promise<IPaymentRoutingData | null> => {
  try {
    const res = await axios.post('api/v1/payment/form-views', data, { params: { page, size, sort } });
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch payment routing");
    return null;
  }
};

export const fetchPaymentRouteRequest = async (id: string): Promise<Partial<IPaymentForm> | null> => {
  try {
    const res = await axios.get(`api/v1/payment/form-views/${id}`);
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch payment route");
    return null;
  }
};

export const fetchSystemGroupsRequest = async (params: IQueryParams): Promise<IPaymentSystemGroupForm[]> => {
  try {
    const res = await axios.get('api/v1/payment/sites/system-group-forms', {
      params,
      paramsSerializer: (par) => qs.stringify(par),
    });
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch system groups");
    return [];
  }
};

export const fetchSystemGroupByIdRequest = async (id: string): Promise<IPaymentSystemGroupForm> => {
  const res = await axios.get(`api/v1/payment/system-group-forms/${id}`);
  return res.data;
};

export const fetchMerchantsRequest = async (): Promise<IMerchant[]> => {
  try {
    const res = await axios.get('api/v1/merchants', { params: { offset: 0, limit: 100 } });
    return res.data?.content;
  } catch (error) {
    NotificationManager.error("Can't fetch merchants");
    return [];
  }
};

export const fetchSitesRequest = async (): Promise<ISite[]> => {
  try {
    const res = await axios.get('api/v1/sites');
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch sites");
    return [];
  }
};

export const fetchPaymentMerchantAccountRequest = async (type: string): Promise<IMerchantAccount[]> => {
  try {
    const res = await axios.get(`api/v1/${type}/merchant-accounts`);
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch merchant accounts");
    return [];
  }
};

export const createPaymentFormDirectRequest = async (data: Partial<IPaymentForm>): Promise<Partial<IPaymentForm>> => {
  try {
    const res = await axios.put(`api/v1/payment/form-views/direct`, data);
    NotificationManager.success('Direct form has been created');
    return res.data;
  } catch (error) {
    const err = error as AxiosError<IErrorResponse>;

    const isErrorWithErrArray = isApiErrorResponse<IErrorResponse>(err.response, ['message', 'code', 'errors']);

    let errText;

    if (isErrorWithErrArray && err.response) {
      errText = err.response.data.errors.map((item) => item?.message).join('\n\r') || '';
    } else {
      errText = err.response?.data.message || '';
    }

    NotificationManager.error(errText);
    throw new Error(err.response?.statusText);
  }
};

export const updatePaymentFormDirectRequest = async (data: Partial<IPaymentForm>): Promise<Partial<IPaymentForm>> => {
  try {
    const payload = { ...data };
    delete payload.id;
    delete payload.identity;
    const res = await axios.post(`api/v1/payment/form-views/direct/${data.id}`, payload);
    NotificationManager.success('Direct form has been updated');
    return res.data;
  } catch (error) {
    const err = error as AxiosError<IErrorResponse>;

    const isErrorWithErrArray = isApiErrorResponse<IErrorResponse>(err.response, ['message', 'code', 'errors']);

    let errText;

    if (isErrorWithErrArray && err.response) {
      errText = err.response.data.errors.map((item) => item?.message).join('\n\r') || '';
    } else {
      errText = err.response?.data.message || '';
    }

    NotificationManager.error(errText);
    throw new Error(err.response?.statusText);
  }
};

export const createPaymentFormRoutingRequest = async (data: Partial<IPaymentForm>): Promise<Partial<IPaymentForm>> => {
  try {
    const res = await axios.put(`api/v1/payment/form-views/routing`, data);
    NotificationManager.success('Routing form has been created');
    return res.data;
  } catch (error) {
    const err = error as AxiosError<IErrorResponse>;

    const isErrorWithErrArray = isApiErrorResponse<IErrorResponse>(err.response, ['message', 'code', 'errors']);

    let errText;

    if (isErrorWithErrArray && err.response) {
      errText = err.response.data.errors.map((item) => item?.message).join('\n\r') || '';
    } else {
      errText = err.response?.data.message || '';
    }

    NotificationManager.error(errText);
    throw new Error(err.response?.statusText);
  }
};

export const updatePaymentFormRoutingRequest = async (data: Partial<IPaymentForm>): Promise<Partial<IPaymentForm>> => {
  try {
    const payload = { ...data };
    delete payload.id;
    delete payload.identity;
    const res = await axios.post(`api/v1/payment/form-views/routing/${data.id}`, payload);
    NotificationManager.success('Routing form has been updated');
    return res.data;
  } catch (error) {
    const err = error as AxiosError<IErrorResponse>;

    const isErrorWithErrArray = isApiErrorResponse<IErrorResponse>(err.response, ['message', 'code', 'errors']);

    let errText;

    if (isErrorWithErrArray) {
      errText = err.response?.data.errors.map((item) => item.message).join('\n\r') || '';
    } else {
      errText = err.response?.data.message || '';
    }

    NotificationManager.error(errText);
    throw new Error(err.response?.statusText);
  }
};

export const updateImagePaymentFormRoutingRequest = async (id?: number, data?: FormData): Promise<void> => {
  return await axios.put(`api/v1/payment/form-views/${id}/logo`, data);
};

export const removeImagePaymentFormRoutingRequest = async (id: string): Promise<void> => {
  return await axios.delete(`api/v1/payment/form-views/${id}/logo`);
};

export const fetchPaymentProvidersSearchRequest = async (): Promise<ISelectOptions[] | []> => {
  try {
    const res = await axios.get(`api/v1/payment/search-filter/provider`, {
      headers: {
        'Content-Type': 'application/json',
      },
      data: {},
    });

    return res.data?.map((provider: string) => ({ value: provider, label: provider }));
  } catch (error) {
    NotificationManager.error("Can't get providers");
    return [];
  }
};

export const fetchPaymentMerchantAccountByFormRequest = async (id?: string): Promise<IMerchantAccount[]> => {
  if (!id) {
    return [];
  }

  try {
    const res = await axios.get(`api/v1/payment/system-group-forms/${id}/merchant-accounts`);
    return res.data;
  } catch (error) {
    NotificationManager.error("Can't fetch merchant accounts");
    return [];
  }
};

export const fetchPaymentMerchantAccountByIdRequest = async (id?: string): Promise<IMerchantAccount> => {
  const res = await axios.get(`api/v1/payment/merchant-accounts/${id}`);
  return res.data;
};

export const togglePaymentRoutingStatusRequest = async ({ id, enabled }: TRoutingStatusParams): Promise<void> => {
  await axios.patch(`api/v1/payment/form-views/${id}/enabled/toggle`, { enabled });
};

export const togglePaymentRoutingArchivingStatus = async ({ id, archived }: TArchivingStatusParams): Promise<void> => {
  await axios.patch(`api/v1/payment/form-views/${id}/archived`, { archived });
};
